import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/use-toast';
import { Upload, Download, AlertCircle, CheckCircle, Plus, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Department } from '@/api/entities';
import { AccountFinal } from '@/api/entities';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function DepartmentDataUploader() {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const { toast } = useToast();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setUploadResults(null);
      loadCSVPreview(selectedFile);
    } else {
      toast({
        title: 'Invalid file',
        description: 'Please select a CSV file',
        variant: 'destructive'
      });
    }
  };

  const loadCSVPreview = async (file) => {
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      const headers = lines[0].split(',').map(h => h.trim());
      
      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim().replace(/^"|"$/g, ''));
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim().replace(/^"|"$/g, ''));
        return result;
      };

      const parsed = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        parsed.push({
          tenant_id: values[0] || '',
          account_id: values[1] || '',
          account_name: values[2] || '',
          department: values[3] || '',
          booking: values[4] || '',
          incumbent_name_department: values[5] || '',
          department_volume: values[6] || '',
          department_signals: values[7] || '',
          incumbent_sentiment_department: values[8] || '',
          sentiment_rationale_department: values[9] || '',
          department_fit_score: values[10] || '',
          department_fit_rationale: values[11] || ''
        });
      }
      
      setDepartments(parsed);
      setShowManualEntry(true);
    } catch (error) {
      console.error('Error parsing CSV:', error);
      toast({
        title: 'Parse error',
        description: 'Failed to parse CSV file',
        variant: 'destructive'
      });
    }
  };

  const downloadTemplate = () => {
    const csv = `tenant_id,account_id,account_name,department,booking,incumbent_name_department,department_volume,department_signals,incumbent_sentiment_department,sentiment_rationale_department,department_fit_score,department_fit_rationale
calmedica,68d02d32902df8ef9ae61ce1,Hospital Example,Cardiology,"Phone, Online, Walk-in",CurrentVendorName,"High volume with 500+ procedures monthly","1) Growing demand 2) New tech adoption",Frustrated,"The current system lacks modern features",85,"Strong fit due to high volume and tech-forward approach"`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'department_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  const addEmptyRow = () => {
    setDepartments([...departments, {
      tenant_id: 'calmedica',
      account_id: '',
      account_name: '',
      department: '',
      booking: '',
      incumbent_name_department: '',
      department_volume: '',
      department_signals: '',
      incumbent_sentiment_department: 'Neutral',
      sentiment_rationale_department: '',
      department_fit_score: '',
      department_fit_rationale: ''
    }]);
  };

  const removeRow = (index) => {
    setDepartments(departments.filter((_, i) => i !== index));
  };

  const updateRow = (index, field, value) => {
    const updated = [...departments];
    updated[index][field] = value;
    setDepartments(updated);
  };

  const handleUpload = async () => {
    if (departments.length === 0) {
      toast({
        title: 'No data',
        description: 'Please add department data to upload',
        variant: 'destructive'
      });
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setUploadResults(null);

    try {
      const allAccounts = await AccountFinal.list();
      const accountsByName = new Map();
      const accountsById = new Map();
      
      allAccounts.forEach(acc => {
        const normalizedName = acc.account_name?.toLowerCase().trim();
        if (normalizedName) accountsByName.set(normalizedName, acc);
        if (acc.account_id) accountsById.set(acc.account_id, acc);
        if (acc.id) accountsById.set(acc.id, acc);
        if (acc.global_pool_account_id) accountsById.set(acc.global_pool_account_id, acc);
      });

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      toast({
        title: 'Processing...',
        description: `Creating ${departments.length} department records`,
        duration: 10000
      });

      for (let i = 0; i < departments.length; i++) {
        try {
          const dept = departments[i];
          
          if (!dept.department || !dept.department.trim()) {
            errorCount++;
            errors.push(`Row ${i + 1}: Missing department name`);
            continue;
          }

          let account = null;
          if (dept.account_id) {
            account = accountsById.get(dept.account_id);
          }
          if (!account && dept.account_name) {
            account = accountsByName.get(dept.account_name.toLowerCase().trim());
          }

          if (!account) {
            errorCount++;
            errors.push(`Row ${i + 1}: Account not found (${dept.account_name || dept.account_id})`);
            continue;
          }

          const departmentData = {
            tenant_id: dept.tenant_id || 'calmedica',
            account_id: account.account_id || account.id,
            account_name: account.account_name,
            department: dept.department,
            booking: dept.booking || null,
            incumbent_name_department: dept.incumbent_name_department || null,
            department_volume: dept.department_volume || null,
            department_signals: dept.department_signals || null,
            incumbent_sentiment_department: dept.incumbent_sentiment_department || null,
            sentiment_rationale_department: dept.sentiment_rationale_department || null,
            department_fit_score: dept.department_fit_score ? parseFloat(dept.department_fit_score) : null,
            department_fit_rationale: dept.department_fit_rationale || null
          };

          await Department.create(departmentData);

          successCount++;
          setProgress(Math.round(((i + 1) / departments.length) * 100));

          if (i < departments.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }

          if ((successCount % 5 === 0) && i < departments.length - 1) {
            toast({
              title: 'Progress...',
              description: `Created ${successCount} of ${departments.length} departments`,
              duration: 3000
            });
          }
        } catch (error) {
          console.error(`Error processing row ${i + 1}:`, error);
          errorCount++;
          errors.push(`Row ${i + 1}: ${error.message}`);
          
          if (error.response?.status === 429) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      setProgress(100);
      setUploadResults({ successCount, errorCount, errors });
      
      if (errorCount === 0) {
        toast({
          title: 'Success!',
          description: `Created ${successCount} department records`,
          duration: 5000
        });
        setDepartments([]);
        setFile(null);
        setShowManualEntry(false);
      } else {
        toast({
          title: 'Partially completed',
          description: `${successCount} succeeded, ${errorCount} failed`,
          variant: 'default',
          duration: 7000
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'An error occurred during upload',
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Department Data Uploader
        </CardTitle>
        <CardDescription>
          Upload department data with full field editing capability
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>CSV Format (12 columns):</strong>
            <br />
            tenant_id, account_id, account_name, department, booking, incumbent_name_department, 
            department_volume, department_signals, incumbent_sentiment_department, 
            sentiment_rationale_department, department_fit_score, department_fit_rationale
            <br />
            <br />
            <strong>Tips:</strong> You can edit all fields in the table below after uploading. Use quotes for multi-line text.
          </AlertDescription>
        </Alert>

        <div className="flex gap-2">
          <div className="flex-1 space-y-2">
            <Label>Download Template</Label>
            <Button onClick={downloadTemplate} variant="outline" className="w-full">
              <Download className="w-4 h-4 mr-2" />
              Download CSV Template
            </Button>
          </div>
          
          <div className="flex-1 space-y-2">
            <Label htmlFor="file">Upload CSV</Label>
            <Input
              id="file"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </div>
        </div>

        {!showManualEntry && (
          <Button onClick={() => { setShowManualEntry(true); addEmptyRow(); }} variant="outline" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Or Enter Manually
          </Button>
        )}

        {showManualEntry && departments.length > 0 && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold">Edit Department Data ({departments.length} rows)</h3>
              <Button onClick={addEmptyRow} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Row
              </Button>
            </div>

            <div className="border rounded-lg overflow-x-auto max-h-[600px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead className="w-[100px]">Tenant</TableHead>
                    <TableHead className="w-[150px]">Account ID</TableHead>
                    <TableHead className="w-[150px]">Account Name</TableHead>
                    <TableHead className="w-[150px]">Department</TableHead>
                    <TableHead className="w-[200px]">Booking</TableHead>
                    <TableHead className="w-[150px]">Incumbent</TableHead>
                    <TableHead className="w-[200px]">Volume</TableHead>
                    <TableHead className="w-[300px]">Signals</TableHead>
                    <TableHead className="w-[150px]">Sentiment</TableHead>
                    <TableHead className="w-[300px]">Sentiment Rationale</TableHead>
                    <TableHead className="w-[100px]">Fit Score</TableHead>
                    <TableHead className="w-[300px]">Fit Rationale</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {departments.map((dept, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Select 
                          value={dept.tenant_id} 
                          onValueChange={(val) => updateRow(index, 'tenant_id', val)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="calmedica">calmedica</SelectItem>
                            <SelectItem value="instructure">instructure</SelectItem>
                            <SelectItem value="demarque">demarque</SelectItem>
                            <SelectItem value="demarque_V2">demarque_V2</SelectItem>
                            <SelectItem value="smowl">smowl</SelectItem>
                            <SelectItem value="diambra">diambra</SelectItem>
                            <SelectItem value="edqwest">edqwest</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={dept.account_id} 
                          onChange={(e) => updateRow(index, 'account_id', e.target.value)}
                          placeholder="Account ID"
                          className="min-w-[140px]"
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={dept.account_name} 
                          onChange={(e) => updateRow(index, 'account_name', e.target.value)}
                          placeholder="Account Name"
                          className="min-w-[140px]"
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={dept.department} 
                          onChange={(e) => updateRow(index, 'department', e.target.value)}
                          placeholder="Department"
                          className="min-w-[140px]"
                        />
                      </TableCell>
                      <TableCell>
                        <Textarea 
                          value={dept.booking} 
                          onChange={(e) => updateRow(index, 'booking', e.target.value)}
                          placeholder="Booking channels"
                          className="min-w-[190px] min-h-[60px]"
                          rows={2}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={dept.incumbent_name_department} 
                          onChange={(e) => updateRow(index, 'incumbent_name_department', e.target.value)}
                          placeholder="Incumbent"
                          className="min-w-[140px]"
                        />
                      </TableCell>
                      <TableCell>
                        <Textarea 
                          value={dept.department_volume} 
                          onChange={(e) => updateRow(index, 'department_volume', e.target.value)}
                          placeholder="Volume & capacity"
                          className="min-w-[190px] min-h-[60px]"
                          rows={2}
                        />
                      </TableCell>
                      <TableCell>
                        <Textarea 
                          value={dept.department_signals} 
                          onChange={(e) => updateRow(index, 'department_signals', e.target.value)}
                          placeholder="1) Signal one 2) Signal two"
                          className="min-w-[290px] min-h-[80px]"
                          rows={3}
                        />
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={dept.incumbent_sentiment_department || 'Neutral'} 
                          onValueChange={(val) => updateRow(index, 'incumbent_sentiment_department', val)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Satisfied">Satisfied</SelectItem>
                            <SelectItem value="Neutral">Neutral</SelectItem>
                            <SelectItem value="Frustrated">Frustrated</SelectItem>
                            <SelectItem value="Exploring alternatives">Exploring alternatives</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Textarea 
                          value={dept.sentiment_rationale_department} 
                          onChange={(e) => updateRow(index, 'sentiment_rationale_department', e.target.value)}
                          placeholder="Sentiment rationale"
                          className="min-w-[290px] min-h-[80px]"
                          rows={3}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          min="0"
                          max="100"
                          value={dept.department_fit_score} 
                          onChange={(e) => updateRow(index, 'department_fit_score', e.target.value)}
                          placeholder="0-100"
                          className="min-w-[90px]"
                        />
                      </TableCell>
                      <TableCell>
                        <Textarea 
                          value={dept.department_fit_rationale} 
                          onChange={(e) => updateRow(index, 'department_fit_rationale', e.target.value)}
                          placeholder="Fit score rationale"
                          className="min-w-[290px] min-h-[80px]"
                          rows={3}
                        />
                      </TableCell>
                      <TableCell>
                        <Button 
                          onClick={() => removeRow(index)} 
                          variant="ghost" 
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {isUploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Uploading...</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>
        )}

        {uploadResults && (
          <Alert className={uploadResults.errorCount === 0 ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}>
            {uploadResults.errorCount === 0 ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-yellow-600" />
            )}
            <AlertDescription>
              <strong>Upload Complete:</strong>
              <br />
              ✅ {uploadResults.successCount} departments created successfully
              {uploadResults.errorCount > 0 && (
                <>
                  <br />
                  ❌ {uploadResults.errorCount} errors
                  {uploadResults.errors.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto">
                      <details className="text-xs">
                        <summary className="cursor-pointer font-medium">View errors</summary>
                        <ul className="mt-2 space-y-1 list-disc list-inside">
                          {uploadResults.errors.slice(0, 20).map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                          {uploadResults.errors.length > 20 && (
                            <li>... and {uploadResults.errors.length - 20} more</li>
                          )}
                        </ul>
                      </details>
                    </div>
                  )}
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {showManualEntry && departments.length > 0 && (
          <Button
            onClick={handleUpload}
            disabled={isUploading}
            className="w-full"
          >
            {isUploading ? (
              'Uploading...'
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload {departments.length} Department{departments.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}